import React, { useEffect, useState } from "react";
import { Megaphone, Share2, X, Check } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

// Slim announcement strip that lives just under the Navbar, on every public page.
// Reads from /api/announcement — hidden entirely when disabled or empty.
// Includes a share button so readers can spread the news.
export default function AnnouncementBanner() {
  const [ann, setAnn] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    api
      .get("/announcement")
      .then((r) => setAnn(r.data))
      .catch(() => setAnn({ is_enabled: false }));
  }, []);

  // Only re-show a dismissed banner if the message itself changes.
  useEffect(() => {
    if (!ann?.message) return;
    try {
      const seen = window.sessionStorage.getItem("bruff_ann_dismissed");
      if (seen && seen === ann.message) setDismissed(true);
    } catch { /* ignore */ }
  }, [ann]);

  if (!ann?.is_enabled || !ann?.message || dismissed) return null;

  const shareUrl =
    typeof window !== "undefined"
      ? `${window.location.origin}${ann.link_url || "/"}`
      : ann.link_url || "/";

  const handleShare = async () => {
    const data = {
      title: "B. Ruff Writes · Announcement",
      text: ann.message,
      url: shareUrl,
    };
    if (navigator.share) {
      try {
        await navigator.share(data);
        return;
      } catch (e) {
        if (e && e.name === "AbortError") return;
      }
    }
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success("Link copied — paste it anywhere!");
      setTimeout(() => setCopied(false), 2500);
    } catch {
      toast.error("Couldn't copy — long-press the address bar and choose Copy.");
    }
  };

  const dismiss = () => {
    setDismissed(true);
    try { window.sessionStorage.setItem("bruff_ann_dismissed", ann.message); } catch { /* ignore */ }
  };

  return (
    <div
      data-testid="announcement-banner"
      className="bg-terracotta text-paper"
    >
      <div className="max-w-6xl mx-auto px-4 lg:px-6 py-2.5 flex items-center gap-3 text-sm">
        <Megaphone size={16} className="shrink-0 opacity-90" />
        <p className="flex-1 leading-snug">
          <span className="font-medium tracking-wide">{ann.message}</span>
          {ann.link_url && ann.link_label && (
            <a
              href={ann.link_url}
              data-testid="announcement-link"
              className="ml-3 underline decoration-paper/60 hover:decoration-paper font-semibold whitespace-nowrap"
            >
              {ann.link_label} →
            </a>
          )}
        </p>
        <button
          onClick={handleShare}
          data-testid="announcement-share-btn"
          title="Share this"
          className="inline-flex items-center gap-1.5 text-[11px] tracking-[0.18em] uppercase px-3 py-1.5 border border-paper/50 hover:bg-paper hover:text-terracotta transition-colors shrink-0"
        >
          {copied ? <Check size={12} /> : <Share2 size={12} />}
          <span className="hidden sm:inline">{copied ? "Copied" : "Share"}</span>
        </button>
        <button
          onClick={dismiss}
          data-testid="announcement-dismiss-btn"
          title="Dismiss"
          aria-label="Dismiss announcement"
          className="text-paper/80 hover:text-paper shrink-0"
        >
          <X size={16} />
        </button>
      </div>
    </div>
  );
}
